<ng-container *ngIf="canRead$ | async">
  <ng-container *ngIf="viewModel$ | async as viewModel">
    <div class="card">
      <div class="p-grid">
        <div class="p-col">
          <h4>BlueSage</h4>
        </div>
        <div class="p-col p-text-right">
          <ng-container *ngIf="viewModel.status">
            <i
              class="p-mr-2"
              [class]="
                viewModel.status.ready
                  ? 'pi pi-check-circle success-text'
                  : 'pi pi-exclamation-circle error-text'
              "
            ></i>
            <span>{{ viewModel.status.ready ? 'Ready to take payments' : 'Not ready' }}</span>
          </ng-container>
          <button
            type="button"
            pButton
            icon="pi pi-refresh"
            class="p-button-text p-ml-3"
            pTooltip="Re-read this zone's BlueSage configuration"
            [disabled]="viewModel.loading || viewModel.saving"
            (click)="store.dispatch(actions.refreshButtonClicked())"
          ></button>
        </div>
      </div>

      <p-messages
        *ngIf="viewModel.status?.missing?.length"
        [severity]="viewModel.status.paymentType.malformed ? 'error' : 'warn'"
        [closable]="false"
        class="p-d-block p-mb-3"
      >
        <ng-template pTemplate>
          <div>
            <div class="p-mb-2">Needs attention on this zone:</div>
            <ul class="p-m-0 p-pl-4">
              <li *ngFor="let item of viewModel.status.missing">{{ item }}</li>
            </ul>
            <button
              *ngIf="viewModel.status.paymentType.malformed && (canConfigure$ | async)"
              type="button"
              pButton
              icon="pi pi-wrench"
              label="Repair payment type"
              class="p-button-sm p-mt-3"
              pTooltip="Rewrites the stored payment type so refunds work. Nothing else changes."
              [disabled]="viewModel.saving"
              (click)="
                store.dispatch(
                  actions.paymentTypeSwitchToggled({
                    enabled: viewModel.status.paymentType.enabled
                  })
                )
              "
            ></button>
          </div>
        </ng-template>
      </p-messages>

      <div class="p-grid" *ngIf="viewModel.status as status">
        <div class="p-col-12 p-md-4">
          <label class="p-d-block p-mb-1">API key</label>
          <i [class]="sourceIcon(status.apiKeySource)" class="p-mr-2"></i>
          <span>{{ describeSource(status.apiKeySource) }}</span>
        </div>
        <div class="p-col-12 p-md-4">
          <label class="p-d-block p-mb-1">Webhook signing secret</label>
          <i [class]="sourceIcon(status.webhookSecretSource)" class="p-mr-2"></i>
          <span>{{ describeSource(status.webhookSecretSource) }}</span>
        </div>
        <div class="p-col-12 p-md-4">
          <!-- Read from BlueSage on save, never entered here: the rate belongs
               to the LOCATION and BlueSage is the authority. A field an admin
               could type would be a second source of truth for a number that
               decides what a customer is shown.

               ⚠ Display and quoting only — money always comes from the event.
               The CRM never computes a differential. -->
          <label class="p-d-block p-mb-1">Dual pricing</label>

          <ng-container *ngIf="status.differentialPctKnown; else rateUnknown">
            <!-- `null` (no dual pricing) and `0` (a card price equal to the cash
                 price) are the same money and different answers. Both are kept. -->
            <ng-container *ngIf="status.differentialPct !== null; else noDualPricing">
              <i class="pi pi-percentage p-mr-2"></i>
              <span><strong>{{ status.differentialPct }}%</strong> card differential</span>
            </ng-container>
            <ng-template #noDualPricing>
              <i class="pi pi-minus-circle p-mr-2 p-text-secondary"></i>
              <span class="p-text-secondary">Not enabled on this location</span>
            </ng-template>

            <small
              class="p-d-block p-text-secondary p-mt-1"
              *ngIf="status.differentialPctCheckedAt"
            >Read {{ status.differentialPctCheckedAt | date: 'medium' }}</small>
          </ng-container>

          <!-- Never rendered as "no dual pricing": we have not asked, which is a
               third answer and a different one. -->
          <ng-template #rateUnknown>
            <i class="pi pi-question-circle p-mr-2 p-text-secondary"></i>
            <span class="p-text-secondary">Not read yet — save the location to fetch it</span>
          </ng-template>
        </div>

        <div class="p-col-12 p-md-4">
          <label class="p-d-block p-mb-1">Payment type</label>
          <ng-container *ngIf="status.paymentType.present; else noPaymentType">
            <p-inputSwitch
              [ngModel]="status.paymentType.enabled"
              [disabled]="viewModel.saving || (canConfigure$ | async) !== true"
              (onChange)="
                store.dispatch(actions.paymentTypeSwitchToggled({ enabled: $event.checked }))
              "
            ></p-inputSwitch>
            <span class="p-ml-2">
              {{ status.paymentType.enabled ? 'Available to operators' : 'Hidden' }}
            </span>
          </ng-container>
          <ng-template #noPaymentType>
            <i class="pi pi-ban error-text p-mr-2"></i>
            <span>Not created yet — save the franchise credentials to create it</span>
          </ng-template>
        </div>

        <!-- Off until BlueSage has enabled online holds for this location:
             before that every hold is refused, and nothing is charged. -->
        <div class="p-col-12 p-md-4">
          <label class="p-d-block p-mb-1">Holds on cards and pay links</label>
          <p-inputSwitch
            [ngModel]="status.holdsEnabled"
            [disabled]="viewModel.saving || (canConfigure$ | async) !== true"
            (onChange)="store.dispatch(actions.holdsSwitchToggled({ enabled: $event.checked }))"
          ></p-inputSwitch>
          <span class="p-ml-2">
            {{ status.holdsEnabled ? 'Operators can hold instead of charging' : 'Charge only' }}
          </span>
          <small class="p-d-block p-text-secondary p-mt-1">
            Adds "Hold instead of charge" to Charge and "Send hold link" to Send pay link.
            A hold is completed or released from the job's transactions.
          </small>
        </div>
      </div>
    </div>

    <ng-container *ngIf="canConfigure$ | async">
      <div class="card">
        <h5>This location</h5>
        <p class="p-mb-3">
          The BlueSage location id for this zone. Inbound events are matched on it, so a wrong
          value stops payments being recorded rather than showing an error.
        </p>
        <div class="p-grid p-formgrid">
          <div class="p-col-12 p-md-6">
            <label for="bluesageLocationId" class="p-d-block p-mb-1">Location id</label>
            <input
              id="bluesageLocationId"
              pInputText
              type="text"
              class="p-d-block"
              placeholder="bsp_loc_XXXXXXXX"
              [ngModel]="viewModel.locationId"
              [disabled]="viewModel.saving"
              (ngModelChange)="store.dispatch(actions.locationIdChanged({ locationId: $event }))"
            />
          </div>
          <div class="p-col-12 p-md-6 p-d-flex p-ai-end">
            <button
              type="button"
              pButton
              icon="pi pi-save"
              label="Save location"
              [disabled]="!viewModel.canSaveLocation"
              (click)="store.dispatch(actions.saveLocationButtonClicked())"
            ></button>
          </div>
        </div>
      </div>

      <div class="card">
        <h5>This franchise</h5>
        <p class="p-mb-3">
          A franchise is one BlueSage organisation, so these are shared by every location under
          it. They cannot be read back —
          <strong>leave a field blank to keep the stored value</strong>.
        </p>
        <div class="p-grid p-formgrid">
          <div class="p-col-12 p-md-6">
            <label for="bluesageApiKey" class="p-d-block p-mb-1">API key</label>
            <input
              id="bluesageApiKey"
              pInputText
              type="password"
              class="p-d-block"
              placeholder="Unchanged"
              autocomplete="off"
              [ngModel]="viewModel.apiKey"
              [disabled]="viewModel.saving"
              (ngModelChange)="store.dispatch(actions.apiKeyChanged({ apiKey: $event }))"
            />
          </div>
          <div class="p-col-12 p-md-6">
            <label for="bluesageWebhookSecret" class="p-d-block p-mb-1">
              Webhook signing secret
            </label>
            <input
              id="bluesageWebhookSecret"
              pInputText
              type="password"
              class="p-d-block"
              placeholder="Unchanged"
              autocomplete="off"
              [ngModel]="viewModel.webhookSigningSecret"
              [disabled]="viewModel.saving"
              (ngModelChange)="
                store.dispatch(
                  actions.webhookSigningSecretChanged({ webhookSigningSecret: $event })
                )
              "
            />
          </div>
          <div class="p-col-12 p-mt-3">
            <button
              type="button"
              pButton
              icon="pi pi-save"
              label="Save credentials"
              [disabled]="!viewModel.canSaveCredentials"
              (click)="store.dispatch(actions.saveCredentialsButtonClicked())"
            ></button>
          </div>
        </div>
      </div>
    </ng-container>
  </ng-container>
</ng-container>
